import React, { useState, ReactNode, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ConfigProvider, Checkbox } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Chevron from '../../atoms/Icon/Chevron';
import ThumbsUp from '../../atoms/Icon/ThumbsUp';
import ThumbsDown from '../../atoms/Icon/ThumbsDown';
import DotsVertical from '../../atoms/Icon/DotsVertical';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import AskAsperAccordionMenu, { AskAsperAccordionMenuItem } from './AskAsperAccordionMenu';
import DislikeFeedbackPopover, { DISLIKE_FEEDBACK_POPOVER_OUTER_WIDTH } from './DislikeFeedbackPopover';

export interface AskAsperAccordionProps {
    /** Title text displayed in the header */
    title: string;
    /** Content to display when expanded */
    children?: ReactNode;
    /** Whether the accordion is initially expanded */
    defaultExpanded?: boolean;
    /** Controlled expanded state */
    expanded?: boolean;
    /** Callback when expand/collapse state changes */
    onExpandChange?: (expanded: boolean) => void;
    /** Menu items for the dots menu dropdown */
    menuItems?: AskAsperAccordionMenuItem[];
    /** Icon for the menu trigger: 'dots' (three dots) or 'export' (export icon). When 'export', shows Export icon next to feedback buttons instead of three dots. */
    menuTriggerIcon?: 'dots' | 'export';
    /** Custom icon to display before the title */
    titleIcon?: ReactNode;
    /** Whether to show the divider line between header and content */
    showDivider?: boolean;
    /** Whether to show default feedback icons (thumbs up/down) */
    showDefaultActions?: boolean;
    /** Callback when thumbs down is clicked */
    onThumbsDown?: () => void;
    /** Callback when thumbs up is clicked */
    onThumbsUp?: () => void;
    /** Callback when dislike feedback is submitted via the popover */
    onDislikeFeedbackSubmit?: (feedback: string) => void;
    /**
     * Label for dislike popover (Describe the issue in "<value>"). Defaults to `title`.
     */
    dislikeFeedbackContextTitle?: string;
    /** Whether to show checkbox for multi-select mode */
    showCheckbox?: boolean;
    /** Whether the checkbox is checked */
    checked?: boolean;
    /** Callback when checkbox state changes */
    onCheckChange?: (checked: boolean) => void;
    /** Additional CSS class name */
    className?: string;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

/** Selected thumb icon stroke + fill (no button background change). */
const THUMB_SELECTED = '#44338A';
const THUMB_NEUTRAL_STROKE = '#71717A';

const AskAsperAccordion: React.FC<AskAsperAccordionProps> = (props) => {
    const {
        title,
        children,
        defaultExpanded = true,
        expanded: controlledExpanded,
        onExpandChange,
        menuItems,
        menuTriggerIcon = 'dots',
        titleIcon,
        showDivider = true,
        showDefaultActions = true,
        onThumbsDown,
        onThumbsUp,
        onDislikeFeedbackSubmit,
        dislikeFeedbackContextTitle,
        showCheckbox = false,
        checked = false,
        onCheckChange,
        className = '',
    } = props;

    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);
    const [vote, setVote] = useState<'up' | 'down' | null>(null);
    const [popoverFixedStyle, setPopoverFixedStyle] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const thumbsDownButtonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;
    const theme = useTheme();
    const dislikePromptTitle = dislikeFeedbackContextTitle ?? title;

    const updatePopoverPosition = useCallback(() => {
        const btn = thumbsDownButtonRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const width = DISLIKE_FEEDBACK_POPOVER_OUTER_WIDTH;
        setPopoverFixedStyle({
            top: r.bottom + 8,
            left: Math.max(8, r.right - width),
        });
    }, []);

    useLayoutEffect(() => {
        if (!showFeedbackPopover) return;
        updatePopoverPosition();
    }, [showFeedbackPopover, updatePopoverPosition]);

    useEffect(() => {
        if (!showFeedbackPopover) return;
        const onScrollOrResize = () => updatePopoverPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [showFeedbackPopover, updatePopoverPosition]);

    useEffect(() => {
        if (!showFeedbackPopover) return;
        let attachedHandler: ((event: MouseEvent) => void) | undefined;
        let cancelled = false;
        // Defer so the same gesture that opened the popover does not immediately close it via mousedown.
        const attachTimer = window.setTimeout(() => {
            if (cancelled) return;
            attachedHandler = (event: MouseEvent) => {
                const t = event.target as Node;
                if (popoverRef.current?.contains(t)) return;
                if (thumbsDownButtonRef.current?.contains(t)) return;
                setShowFeedbackPopover(false);
            };
            document.addEventListener('mousedown', attachedHandler);
        }, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(attachTimer);
            if (attachedHandler) {
                document.removeEventListener('mousedown', attachedHandler);
            }
        };
    }, [showFeedbackPopover]);

    const openDislikePopover = useCallback(() => {
        updatePopoverPosition();
        window.setTimeout(() => {
            setShowFeedbackPopover(true);
        }, 0);
    }, [updatePopoverPosition]);

    const handleThumbsDownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onThumbsDown?.();

        /* Always open feedback UI on dislike; onDislikeFeedbackSubmit is optional (e.g. host wiring later). */
        if (showFeedbackPopover) {
            setShowFeedbackPopover(false);
            setVote(null);
            return;
        }

        setVote('down');
        openDislikePopover();
    };

    const handleThumbsUpClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onThumbsUp?.();
        setShowFeedbackPopover(false);
        setVote((v) => (v === 'up' ? null : 'up'));
    };

    const handleFeedbackSubmit = (feedback: string) => {
        onDislikeFeedbackSubmit?.(feedback);
        setShowFeedbackPopover(false);
    };

    const handleFeedbackClose = () => {
        setShowFeedbackPopover(false);
    };

    const handleToggle = () => {
        const newExpanded = !isExpanded;
        if (!isControlled) {
            setInternalExpanded(newExpanded);
        }
        onExpandChange?.(newExpanded);
    };

    const handleCheckboxChange = (e: { stopPropagation: () => void; target: { checked: boolean } }) => {
        e.stopPropagation();
        onCheckChange?.(e.target.checked);
    };

    const upSelected = vote === 'up';
    const downSelected = vote === 'down' || showFeedbackPopover;

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`bg-white border border-solid flex flex-col gap-5 items-start py-4 rounded-lg w-full ${
                    checked ? 'border-[#5f49bc] bg-[#fbfaff]' : 'border-[#e4e4e7]'
                } ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-ask-asper-accordion
            >
                {/* Header section */}
                <div className="flex flex-col gap-4 items-start w-full">
                    <div className="flex items-start px-5 w-full">
                        <div className="flex items-center justify-between w-full">
                            {/* Left side: Checkbox, Title with optional icon */}
                            <div className="flex gap-3 items-center">
                                {showCheckbox && (
                                    <Checkbox
                                        checked={checked}
                                        onChange={handleCheckboxChange}
                                        onClick={(e) => e.stopPropagation()}
                                        className="[&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#5f49bc] [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#5f49bc]"
                                    />
                                )}
                                {titleIcon}
                                <span
                                    className="font-['Open_Sans'] text-[16px] leading-normal text-[#140b1d]"
                                    style={{ fontWeight: 600 }}
                                >
                                    {title}
                                </span>
                            </div>

                            {/* Right side: Actions and chevron */}
                            <div className="flex gap-4 items-center justify-end">
                                {(showDefaultActions || menuItems) && (
                                    <div className="flex gap-1 items-center justify-end">
                                        {showDefaultActions && (
                                            <>
                                                <div className="relative">
                                                    <div className="border border-[#e4e4e7] border-solid rounded flex items-center overflow-hidden">
                                                        <button
                                                            type="button"
                                                            className="bg-white flex items-center justify-center p-0 size-6 cursor-pointer hover:bg-gray-50 border-none"
                                                            onClick={handleThumbsUpClick}
                                                            aria-label="Thumbs up"
                                                            aria-pressed={upSelected}
                                                        >
                                                            <ThumbsUp
                                                                size={16}
                                                                strokeColor={upSelected ? THUMB_SELECTED : THUMB_NEUTRAL_STROKE}
                                                                color={upSelected ? THUMB_SELECTED : 'transparent'}
                                                                strokeWidth={1.5}
                                                            />
                                                        </button>
                                                        <div className="w-px h-6 bg-[#e4e4e7]" />
                                                        <button
                                                            ref={thumbsDownButtonRef}
                                                            type="button"
                                                            className="bg-white flex items-center justify-center p-0 size-6 cursor-pointer hover:bg-gray-50 border-none"
                                                            onClick={handleThumbsDownClick}
                                                            aria-label="Thumbs down"
                                                            aria-expanded={showFeedbackPopover}
                                                            aria-pressed={downSelected}
                                                        >
                                                            <ThumbsDown
                                                                size={16}
                                                                strokeColor={
                                                                    downSelected ? THUMB_SELECTED : THUMB_NEUTRAL_STROKE
                                                                }
                                                                color={downSelected ? THUMB_SELECTED : 'transparent'}
                                                                strokeWidth={1.5}
                                                            />
                                                        </button>
                                                    </div>

                                                    {showFeedbackPopover &&
                                                        typeof document !== 'undefined' &&
                                                        createPortal(
                                                            <div
                                                                ref={popoverRef}
                                                                className="fixed z-[10050] pointer-events-auto"
                                                                style={{
                                                                    top: popoverFixedStyle.top,
                                                                    left: popoverFixedStyle.left,
                                                                }}
                                                            >
                                                                <DislikeFeedbackPopover
                                                                    open={showFeedbackPopover}
                                                                    onClose={handleFeedbackClose}
                                                                    onSubmit={handleFeedbackSubmit}
                                                                    contextTitle={dislikePromptTitle}
                                                                    data-test-id={`${props['data-test-id']}-dislike-feedback`}
                                                                />
                                                            </div>,
                                                            document.body,
                                                        )}
                                                </div>
                                            </>
                                        )}

                                        {menuItems && menuItems.length > 0 && (
                                            <AskAsperAccordionMenu items={menuItems} placement="bottomRight">
                                                <button
                                                    type="button"
                                                    className="bg-white border border-[#e4e4e7] border-solid flex items-center justify-center rounded p-0 size-6 cursor-pointer hover:bg-gray-50"
                                                    aria-label={menuTriggerIcon === 'export' ? 'Export' : 'More options'}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {menuTriggerIcon === 'export' ? (
                                                        <DownloadFile size={12} color="#71717A" />
                                                    ) : (
                                                        <DotsVertical size={12} color="#71717A" rotate={90} />
                                                    )}
                                                </button>
                                            </AskAsperAccordionMenu>
                                        )}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center size-6"
                                    onClick={handleToggle}
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                                >
                                    <Chevron size={10} color="#71717a" rotate={isExpanded ? 0 : 180} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {isExpanded && showDivider && <div className="h-px bg-[#e4e4e7] w-full" />}
                </div>

                {isExpanded && children && <div className="w-full">{children}</div>}
            </div>
        </ConfigProvider>
    );
};

export default AskAsperAccordion;
